// src/components/Contatos.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  HiOutlineX,
  HiOutlineSearch,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineDuplicate,
  HiOutlineIdentification,
} from "react-icons/hi";
import { FaWhatsapp } from "react-icons/fa";
import { supabase } from "../supabaseClient";
import "./Contatos.css";

const TIPOS = [
  { valor: "mao_de_obra", label: "Mão de obra" },
  { valor: "material", label: "Material" },
  { valor: "material_mao_de_obra", label: "Material e Mão de obra" },
];

const FORM_VAZIO = {
  id: null,
  nome: "",
  empresa: "",
  cnpj: "",
  whatsapp: "",
  email: "",
  tipo: "mao_de_obra",
  tags: "",
  coopercon: false,
  qualifios: false,
};

const formatarCnpj = (digits) =>
  digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");

// Preposições que ficam em minúsculo (exceto na primeira palavra)
const PALAVRAS_MINUSCULAS = ["de", "da", "do", "das", "dos", "e"];
// Siglas societárias que ficam em maiúsculo
const SIGLAS = ["ltda", "me", "epp", "eireli", "mei", "sa", "s/a", "ss", "s/s"];

// "ACME COMERCIO DE MATERIAIS LTDA" -> "Acme Comercio de Materiais LTDA"
const capitalizarNome = (texto) => {
  if (!texto) return "";
  return texto
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((palavra, i) => {
      if (SIGLAS.includes(palavra)) return palavra.toUpperCase();
      if (i > 0 && PALAVRAS_MINUSCULAS.includes(palavra)) return palavra;
      return palavra.replace(/(^|[.\-/(])(\p{L})/gu, (_, sep, letra) => sep + letra.toUpperCase());
    })
    .join(" ");
};

// "60115170" -> "60115-170"
const formatarCep = (cep) => {
  const digits = (cep || "").replace(/\D/g, "");
  if (digits.length !== 8) return cep || "";
  return digits.replace(/^(\d{5})(\d{3})$/, "$1-$2");
};

// Endereço sem o CEP (o CEP é exibido separadamente)
const montarEndereco = (e) => {
  if (!e) return "";
  const partes = [
    [e.tipo_logradouro, e.logradouro].filter(Boolean).join(" "),
    e.numero,
    e.complemento,
    e.bairro,
    e.cidade?.nome,
    e.estado?.sigla,
    e.pais?.nome,
  ].filter(Boolean);
  return partes.join(", ");
};

export default function Contatos({ onClose, user, containerAtual }) {
  const [aba, setAba] = useState("meus"); // "meus" | "container"
  const [showForm, setShowForm] = useState(false);
  const [showCnpjResultado, setShowCnpjResultado] = useState(false);
  const [cnpjDados, setCnpjDados] = useState(null);

  const [showBuscaCnpj, setShowBuscaCnpj] = useState(false);
  const [cnpjBusca, setCnpjBusca] = useState("");
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [erroCnpj, setErroCnpj] = useState("");
  const [copiadoCampo, setCopiadoCampo] = useState(null);

  const [busca, setBusca] = useState("");
  const [fornecedores, setFornecedores] = useState([]);
  const [fornecedoresContainer, setFornecedoresContainer] = useState([]);
  const [form, setForm] = useState(FORM_VAZIO);
  const [copiadoId, setCopiadoId] = useState(null);

  const isOutroContainer = containerAtual && containerAtual !== user?.id;

  const buscarFornecedores = useCallback(async () => {
    if (!user?.id) return;
    const { data, error } = await supabase
      .from("fornecedores")
      .select("*")
      .eq("usuario_id", user.id)
      .order("nome");
    if (error) {
      console.error("Erro ao buscar fornecedores:", error);
      return;
    }
    setFornecedores(data || []);
  }, [user?.id]);

  const buscarFornecedoresContainer = useCallback(async () => {
    if (!isOutroContainer) {
      setFornecedoresContainer([]);
      return;
    }
    const { data, error } = await supabase
      .from("fornecedores")
      .select("*")
      .eq("usuario_id", containerAtual)
      .order("nome");
    if (error) {
      console.error("Erro ao buscar fornecedores do container:", error);
      return;
    }
    setFornecedoresContainer(data || []);
  }, [containerAtual, isOutroContainer]);

  useEffect(() => {
    buscarFornecedores();
    buscarFornecedoresContainer();
  }, [buscarFornecedores, buscarFornecedoresContainer]);

  useEffect(() => {
    if (!isOutroContainer) setAba("meus");
  }, [isOutroContainer, containerAtual]);

  const listaAtiva = aba === "meus" ? fornecedores : fornecedoresContainer;

  const fornecedoresFiltrados = listaAtiva.filter((f) => {
    if (!busca.trim()) return true;
    const termo = busca.toLowerCase();
    return (
      f.nome?.toLowerCase().includes(termo) ||
      f.empresa?.toLowerCase().includes(termo) ||
      f.tags?.some((tag) => tag.toLowerCase().includes(termo))
    );
  });

  const abrirNovo = () => {
    setForm(FORM_VAZIO);
    setShowCnpjResultado(false);
    setShowForm(true);
  };

  const abrirEdicao = (f) => {
    if (aba !== "meus") return;
    setForm({
      id: f.id,
      nome: f.nome,
      empresa: f.empresa,
      cnpj: f.cnpj || "",
      whatsapp: f.whatsapp,
      email: f.email || "",
      tipo: f.tipo || "mao_de_obra",
      tags: f.tags?.join(" ") || "",
      coopercon: f.coopercon || false,
      qualifios: f.qualifios || false,
    });
    setShowCnpjResultado(false);
    setShowForm(true);
  };

  const buscarCnpj = async () => {
    const cnpjLimpo = cnpjBusca.replace(/\D/g, "");
    if (cnpjLimpo.length !== 14) {
      setErroCnpj("CNPJ inválido.");
      return;
    }
    setBuscandoCnpj(true);
    setErroCnpj("");
    try {
      const resp = await fetch(`https://publica.cnpj.ws/cnpj/${cnpjLimpo}`);
      if (!resp.ok) {
        if (resp.status === 429) throw new Error("Limite de consultas atingido. Aguarde 1 minuto.");
        throw new Error("CNPJ não encontrado.");
      }
      const data = await resp.json();
      const estab = data.estabelecimento || {};

      setCnpjDados({
        cnpjNumeros: cnpjLimpo,
        cnpjFormatado: formatarCnpj(cnpjLimpo),
        razaoSocial: data.razao_social ? capitalizarNome(data.razao_social) : "—",
        nomeFantasia: estab.nome_fantasia || "—",
        endereco: montarEndereco(estab) || "—",
        cep: formatarCep(estab.cep) || "—",
        telefone: estab.ddd1 && estab.telefone1 ? `(${estab.ddd1}) ${estab.telefone1}` : "—",
        inscricaoEstadual: estab.inscricoes_estaduais?.[0]?.inscricao_estadual || "—",
        codigoSped: estab.cidade?.siafi_id || "—",
        email: estab.email || "",
      });

      setShowBuscaCnpj(false);
      setCnpjBusca("");
      setShowForm(false);
      setShowCnpjResultado(true);
    } catch (err) {
      setErroCnpj(err.message || "Erro ao buscar CNPJ.");
    } finally {
      setBuscandoCnpj(false);
    }
  };

  const copiarCampo = (chave, valor) => {
    navigator.clipboard.writeText(valor).then(() => {
      setCopiadoCampo(chave);
      setTimeout(() => setCopiadoCampo(null), 1500);
    });
  };

  const usarDadosCnpj = () => {
    setForm({
      ...FORM_VAZIO,
      empresa: cnpjDados.nomeFantasia !== "—" ? cnpjDados.nomeFantasia : cnpjDados.razaoSocial,
      cnpj: cnpjDados.cnpjNumeros,
      email: cnpjDados.email,
    });
    setShowCnpjResultado(false);
    setShowForm(true);
  };

  const salvar = async (e) => {
    e.preventDefault();

    const numeroLimpo = form.whatsapp.replace(/\D/g, "");
    if (!/^\d{10,11}$/.test(numeroLimpo)) {
      alert("WhatsApp inválido. Use DDD + número.");
      return;
    }
    const whatsappFormatado = `(${numeroLimpo.slice(0, 2)}) ${numeroLimpo.slice(2)}`;

    const tagsArray = form.tags
      .split(/[\s,]+/)
      .filter((t) => t.startsWith("#"))
      .map((t) => t.toLowerCase());

    const payload = {
      nome: form.nome,
      empresa: form.empresa,
      cnpj: form.cnpj,
      whatsapp: whatsappFormatado,
      email: form.email,
      tipo: form.tipo,
      tags: tagsArray,
      coopercon: form.coopercon,
      qualifios: form.qualifios,
    };

    const donoAlvo = aba === "meus" ? user.id : containerAtual;

    const { error } = form.id
      ? await supabase.from("fornecedores").update(payload).eq("id", form.id)
      : await supabase.from("fornecedores").insert([{ ...payload, usuario_id: donoAlvo }]);

    if (error) {
      alert("Erro ao salvar: " + error.message);
      return;
    }

    setShowForm(false);
    buscarFornecedores();
    buscarFornecedoresContainer();
  };

  const excluir = async (id) => {
    if (aba !== "meus") return;
    if (!window.confirm("Excluir este contato?")) return;
    const { error } = await supabase.from("fornecedores").delete().eq("id", id);
    if (!error) buscarFornecedores();
  };

  const copiar = (f) => {
    const texto = `Nome: ${f.nome}\nEmpresa: ${f.empresa}\nCNPJ: ${f.cnpj || "-"}\nWhatsApp: ${f.whatsapp}\nE-mail: ${f.email || "-"}`;
    navigator.clipboard.writeText(texto).then(() => {
      setCopiadoId(f.id);
      setTimeout(() => setCopiadoId(null), 2000);
    });
  };

  return (
    <>
      <div className="contatos-overlay" onClick={onClose} />

      <div className="contatos-painel">
        <div className="contatos-header">
          <h3>Contatos</h3>
          <div className="contatos-header-actions">
            <button
              className={`contatos-icon-btn ${showBuscaCnpj ? "contatos-icon-btn--active" : ""}`}
              title="Buscar CNPJ"
              onClick={() => setShowBuscaCnpj((v) => !v)}
            >
              <HiOutlineIdentification size={17} />
            </button>
            <button
              className="contatos-icon-btn contatos-icon-btn--primary"
              title="Novo contato"
              onClick={abrirNovo}
            >
              <HiOutlinePlus size={17} />
            </button>
            <button className="contatos-icon-btn" title="Fechar" onClick={onClose}>
              <HiOutlineX size={17} />
            </button>
          </div>
        </div>

        {showBuscaCnpj && (
          <div className="contatos-busca-cnpj">
            <input
              type="text"
              placeholder="Digite o CNPJ"
              value={cnpjBusca}
              onChange={(e) => setCnpjBusca(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && buscarCnpj()}
            />
            <button onClick={buscarCnpj} disabled={buscandoCnpj}>
              {buscandoCnpj ? "..." : "Buscar"}
            </button>
            {erroCnpj && <span className="contatos-busca-cnpj-erro">{erroCnpj}</span>}
          </div>
        )}

        {isOutroContainer && (
          <div className="contatos-tabs">
            <button
              className={`contatos-tab ${aba === "meus" ? "contatos-tab--active" : ""}`}
              onClick={() => setAba("meus")}
            >
              Meus contatos
            </button>
            <button
              className={`contatos-tab ${aba === "container" ? "contatos-tab--active" : ""}`}
              onClick={() => setAba("container")}
            >
              Contatos do container
            </button>
          </div>
        )}

        <div className="contatos-busca-wrapper">
          <HiOutlineSearch className="contatos-busca-icon" size={16} />
          <input
            className="contatos-busca-input"
            type="text"
            placeholder="Buscar nome, empresa ou #tag"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          {busca && (
            <button className="contatos-busca-clear" onClick={() => setBusca("")}>×</button>
          )}
        </div>

        <div className="contatos-lista-container">
          <div className="contatos-lista">
            {fornecedoresFiltrados.length === 0 && (
              <p className="contatos-vazio">Nenhum contato encontrado.</p>
            )}
            {fornecedoresFiltrados.map((f) => (
              <div key={f.id} className="contato-item">
                <div className="contato-info">
                  <strong>{f.nome}</strong>
                  <div className="contato-empresa">{f.empresa}</div>
                  {f.cnpj && <div className="contato-cnpj">{f.cnpj}</div>}
                  <div className="contato-whatsapp">
                    <a href={`https://wa.me/${f.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer">
                      <FaWhatsapp />
                    </a>
                    <span>{f.whatsapp}</span>
                  </div>
                  {f.email && <div className="contato-email">{f.email}</div>}
                  {f.tags?.length > 0 && (
                    <div className="contato-tags">
                      {f.tags.map((tag) => (
                        <button key={tag} className="contato-tag" onClick={() => setBusca(tag)}>
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="contato-actions">
                  <button className="contato-action-btn" title="Copiar" onClick={() => copiar(f)}>
                    <HiOutlineDuplicate size={16} style={{ color: copiadoId === f.id ? "#22c55e" : undefined }} />
                  </button>
                  {aba === "meus" && (
                    <>
                      <button className="contato-action-btn" title="Editar" onClick={() => abrirEdicao(f)}>
                        <HiOutlinePencil size={16} />
                      </button>
                      <button className="contato-action-btn contato-action-btn--danger" title="Excluir" onClick={() => excluir(f.id)}>
                        <HiOutlineTrash size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="contatos-footer">powered by Fonecta</div>
      </div>

      {showCnpjResultado && cnpjDados && (
        <div className="contatos-cnpj-painel">
          <div className="contatos-header">
            <h3>Dados do CNPJ</h3>
            <div className="contatos-header-actions">
              <button className="contatos-icon-btn" title="Fechar" onClick={() => setShowCnpjResultado(false)}>
                <HiOutlineX size={17} />
              </button>
            </div>
          </div>

          <div className="contatos-cnpj-lista">
            {[
              ["cnpjNumeros", "CNPJ (números)", cnpjDados.cnpjNumeros],
              ["cnpjFormatado", "CNPJ formatado", cnpjDados.cnpjFormatado],
              ["razaoSocial", "Razão social", cnpjDados.razaoSocial],
              ["nomeFantasia", "Nome fantasia", cnpjDados.nomeFantasia],
              ["endereco", "Endereço", cnpjDados.endereco],
              ["cep", "CEP", cnpjDados.cep],
              ["telefone", "Telefone", cnpjDados.telefone],
              ["inscricaoEstadual", "Inscrição estadual", cnpjDados.inscricaoEstadual],
              ["codigoSped", "Código SPED da cidade", cnpjDados.codigoSped],
            ].map(([chave, label, valor]) => (
              <div className="contatos-cnpj-row" key={chave}>
                <div className="contatos-cnpj-row-text">
                  <span className="contatos-cnpj-row-label">{label}</span>
                  <span className="contatos-cnpj-row-value">{valor}</span>
                </div>
                <button
                  className="contatos-cnpj-copy-btn"
                  title="Copiar"
                  onClick={() => copiarCampo(chave, valor)}
                >
                  <HiOutlineDuplicate size={15} style={{ color: copiadoCampo === chave ? "#22c55e" : undefined }} />
                </button>
              </div>
            ))}
          </div>

          <div className="contatos-cnpj-footer">
            <button onClick={usarDadosCnpj}>Usar dados e cadastrar contato</button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="contatos-form-painel">
          <div className="contatos-header">
            <h3>{form.id ? "Editar contato" : "Novo contato"}</h3>
            <div className="contatos-header-actions">
              <button className="contatos-icon-btn" title="Fechar" onClick={() => setShowForm(false)}>
                <HiOutlineX size={17} />
              </button>
            </div>
          </div>

          <form className="contatos-form" onSubmit={salvar}>
            <div className="contatos-form-group">
              <span className="contatos-form-label">Nome</span>
              <input
                type="text"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                required
              />
            </div>

            <div className="contatos-form-group">
              <span className="contatos-form-label">Empresa</span>
              <input
                type="text"
                value={form.empresa}
                onChange={(e) => setForm({ ...form, empresa: e.target.value })}
                required
              />
            </div>

            <div className="contatos-form-group">
              <span className="contatos-form-label">CNPJ</span>
              <input
                type="text"
                value={form.cnpj}
                onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                placeholder="00.000.000/0000-00"
              />
            </div>

            <div className="contatos-form-group">
              <span className="contatos-form-label">WhatsApp (DDD + número)</span>
              <input
                type="text"
                value={form.whatsapp}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                required
              />
            </div>

            <div className="contatos-form-group">
              <span className="contatos-form-label">E-mail</span>
              <input
                type="text"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="contato@empresa.com"
              />
            </div>

            <div className="contatos-tipo-group">
              {TIPOS.map((t) => (
                <button
                  type="button"
                  key={t.valor}
                  className={`contatos-tipo-btn ${form.tipo === t.valor ? "selected" : ""}`}
                  onClick={() => setForm({ ...form, tipo: t.valor })}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <label className="contatos-check">
              <input
                type="checkbox"
                checked={form.coopercon}
                onChange={(e) => setForm({ ...form, coopercon: e.target.checked })}
              />
              Fornecedor Coopercon?
            </label>

            <label className="contatos-check">
              <input
                type="checkbox"
                checked={form.qualifios}
                onChange={(e) => setForm({ ...form, qualifios: e.target.checked })}
              />
              Associado à Qualifios?
            </label>

            <div className="contatos-form-group">
              <span className="contatos-form-label">Hashtags</span>
              <input
                type="text"
                placeholder="#cimento #areia"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
              />
            </div>

            <div className="contatos-form-actions">
              <button type="submit" className="contatos-btn-salvar">Salvar</button>
              <button type="button" className="contatos-btn-cancelar" onClick={() => setShowForm(false)}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}